import { memo, useRef, useState } from 'react';
import { z } from 'zod';
import { IconPicker } from '~/components/chat/commands/IconPicker';
import { KeyboardShortcut } from '~/components/chat/commands/KeyboardShortcut';
import * as PopupForm from '~/components/chat/commands/PopupForm';
import View from '~/components/chat/commands/View';
import type { PromptCommandCategory } from '~/lib/stores/command/models/PromptCommandCategory';
import type { UserPromptCommand } from '~/lib/stores/command/models/UserPromptCommand';
import { validateFormData } from '~/utils/form';

interface Props {
  command: UserPromptCommand;
  onCancel: () => void;
  onSave: (values: { name: string; prompt: string; category?: string; icon?: string }) => void;
  categories: PromptCommandCategory[];
}

export const PromptEdit = memo<Props>(({ command, onCancel, onSave, categories }) => {
  const schema = z.object({
    name: z.string().min(1, 'Name is required').max(100, 'Name must be at most 100 characters long'),
    prompt: z.string().min(1, 'Prompt is required'),
    category: z.string().optional(),
    icon: z.string().optional(),
  });

  const formRef = useRef<HTMLFormElement>(null);
  const [errors, setErrors] = useState<Partial<z.infer<typeof schema>>>({});

  const [icon, setIcon] = useState(command.icon || '');

  function handleSave() {
    const formData = new FormData(formRef.current!);

    formData.set('icon', icon);

    validateFormData(formData, schema, {
      onSuccess: (data) => onSave(data),
      onError: (errors) => setErrors(errors),
    });
  }

  return (
    <View.Container>
      <View.Header title="Edit Prompt" />

      <View.Content>
        <PopupForm.Root ref={formRef}>
          <div className="flex gap-2">
            <IconPicker name="icon" label="Icon" value={icon} onValueChange={setIcon} />
            <PopupForm.Input
              defaultValue={command.name}
              label="Name"
              size="sm"
              name="name"
              autoFocus
              placeholder="Name your prompt..."
              className="w-full text-sm"
              error={errors.name}
              onChange={() => setErrors(({ name, ...rest }) => rest)}
            />
          </div>

          <PopupForm.Select
            defaultValue={command.category?.id}
            name="category"
            label="Category"
            placeholder="Select a category"
            options={categories.map((category) => ({ value: category.id, label: category.name }))}
          />
          <PopupForm.Textarea
            label="Prompt"
            defaultValue={command.prompt}
            name="prompt"
            placeholder="Write your prompt..."
            error={errors.prompt}
            onChange={() => setErrors(({ prompt, ...rest }) => rest)}
          />
        </PopupForm.Root>
      </View.Content>
      <View.Footer>
        <div className="flex-1"></div>
        <KeyboardShortcut label="Cancel" keys={['Esc']} action={onCancel} />
        <KeyboardShortcut label="Save" keys={['Meta', 'Enter']} action={handleSave} />
      </View.Footer>
    </View.Container>
  );
});
